import { Box, IconButton, InputAdornment, Skeleton, Stack, TextField } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import type { GridApiPro, GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import { VyIcon } from '@vy/ui-design-system';
import { getSelectorAttribute, vyTableSelectors } from '@vy/app-selectors';
import React, { Suspense, useMemo, useRef } from 'react';
import { SearchIcon } from '../../../../components/icons';

import { useGridColumnSettings } from '../../../../hooks/use-grid-column-settings';
import { useTableSorting } from '../../../../hooks/use-table-sorting';
import type { FilterField, FilterLine } from '../../vyTableFilter/types';
import { VyTableColumnSettings } from '../table-column-settings';
import { VyTableColumnSettingsButton } from '../vy-table-column-settings-button';
import { VyTableFilterNew } from '../vy-table-filter-new';
import { VyTable } from '../vy-table/VyTable';
import type { VyTableMinimalProps } from '../vy-table/vy-table-types';

const TOOLBAR_SLOT_KEYS = ['dateRangePicker', 'search', 'filters'] as const;
type ToolbarSlotKey = (typeof TOOLBAR_SLOT_KEYS)[number];

const DEFAULT_TOOLBAR_ORDER: ToolbarSlotKey[] = ['dateRangePicker', 'search', 'filters'];

type VyTableWithFiltersProps<Item extends Omit<GridValidRowModel, 'rows'>> = VyTableMinimalProps<Item> & {
  apiRef: React.MutableRefObject<GridApiPro>;
  columns: GridColDef<Item>[];
  tableName: string;
  search?: {
    value?: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
  };
  filters?: {
    fields: FilterField[];
    onFiltersChange?: (filters: FilterLine[]) => void;
    defaultFilters?: FilterLine[];
  };
  columnSettings?: {
    excludedFields?: string[];
    mandatoryColumnsCount?: number;
  };
  onDownload?: () => void | Promise<void>;
  onRefresh?: () => void | Promise<void>;
  actions?: React.ReactNode;
  tableSx?: VyTableMinimalProps<Item>['sx'];
  isFilterOpen?: boolean;
  dateRangePicker?: React.ReactNode;
  toolbarOrder?: ToolbarSlotKey[];
  contentBetweenToolbarAndTable?: React.ReactNode;
  tableFooter?: React.ReactNode;
};

export function VyTableWithFilters<Item extends Omit<GridValidRowModel, 'rows'>>({
  apiRef,
  columns,
  tableName,
  search,
  filters,
  columnSettings,
  onDownload,
  onRefresh,
  actions,
  tableSx,
  isFilterOpen = false,
  dateRangePicker,
  toolbarOrder = DEFAULT_TOOLBAR_ORDER,
  contentBetweenToolbarAndTable,
  tableFooter,
  ...tableProps
}: VyTableWithFiltersProps<Item>) {
  const columnsSignature = useMemo(() => {
    return columns.map(col => col.field).filter(Boolean).sort().join(',')
  }, [columns]);

  const stableColumnsRef = useRef<{ signature: string; columns: GridColDef<Item>[] }>({
    signature: '',
    columns,
  });

  const stableColumns = useMemo(() => {
    if (columnsSignature === stableColumnsRef.current.signature) {
      return stableColumnsRef.current.columns;
    }
    stableColumnsRef.current = { signature: columnsSignature, columns };
    return columns;
  }, [columnsSignature, columns]);

  const {
    columnSettings: columnSettingsState,
    handleColumnVisibilityChange,
    isSettingsOpen,
    openSettings,
    closeSettings,
  } = useGridColumnSettings(apiRef, stableColumns, columnSettings?.excludedFields);

  const { handleSortModelChange } = useTableSorting();

  const anchorRef = useRef<HTMLElement | null>(null);

  const handleOpenColumnsSettings = (event: React.MouseEvent<HTMLButtonElement>) => {
    anchorRef.current = event.currentTarget;
    openSettings();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Stack
        direction="row"
        sx={{
          padding: 2,
          gap: "10px",
          justifyContent: "space-between"
        }}>
        <Stack direction="row" sx={{
          gap: "10px"
        }}>
          {toolbarOrder.map((slot) => {
            if (slot === 'search' && search) {
              return (
                <TextField
                  key="search"
                  size="small"
                  label="Search"
                  placeholder={search.placeholder ?? 'Search...'}
                  value={search.value ?? ''}
                  sx={{
                    paddingBottom: 0,
                    width: '300px',
                    '& .MuiFilledInput-root': {
                      height: '40px',
                      backgroundColor: '#F5F6FA',
                      borderRadius: '4px',
                    },
                    '& .css-1ssscdh-MuiInputBase-input-MuiFilledInput-input': {
                      paddingBottom: '5px',
                    },
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color={blueGrey[400]} />
                        </InputAdornment>
                      ),
                    },

                    htmlInput: getSelectorAttribute(vyTableSelectors.searchInput)
                  }}
                  onChange={search.onChange} />
              );
            }

            if (slot === 'dateRangePicker' && dateRangePicker) {
              return (
                <Box
                  key="dateRangePicker"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end"
                  }}>
                  {dateRangePicker}
                </Box>
              );
            }

            if (slot === 'filters' && filters) {
              return (
                <Box
                  key="filters"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end"
                  }}>
                  <VyTableFilterNew
                    fields={filters.fields}
                    onFiltersChange={filters.onFiltersChange}
                    defaultFilters={filters.defaultFilters}
                  />
                </Box>
              );
            }

            return null;
          })}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end"
            }}>
            <VyTableColumnSettingsButton onClick={handleOpenColumnsSettings} />

            <VyTableColumnSettings
              anchorEl={anchorRef.current}
              open={isSettingsOpen}
              onClose={closeSettings}
              columns={columnSettingsState}
              handleColumnVisibilityChange={handleColumnVisibilityChange}
              mandatoryColumnsCount={columnSettings?.mandatoryColumnsCount ?? 2}
            />
          </Box>
        </Stack>

        <Stack
          direction="row"
          sx={{
            alignContent: "end",
            gap: "8px",
            alignItems: "flex-end"
          }}>
          {(onDownload || onRefresh) && (
            <Stack direction="row" sx={{
              gap: "8px"
            }}>
              {onRefresh && (
                <IconButton
                  onClick={onRefresh}
                  sx={{
                    border: '1px solid #0D1B273B',
                    borderRadius: '4px',
                    padding: '8px',
                    width: '36px',
                    height: '36px',
                    '&:hover': {
                      backgroundColor: '#F5F6FA',
                    },
                  }}
                >
                  <VyIcon icon="Refresh" />
                </IconButton>
              )}
              {onDownload && (
                <IconButton
                  onClick={onDownload}
                  sx={{
                    border: '1px solid #0D1B273B',
                    borderRadius: '4px',
                    padding: '8px',
                    width: '36px',
                    height: '36px',
                    '&:hover': {
                      backgroundColor: '#F5F6FA',
                    },
                  }}
                >
                  <VyIcon
                    icon="Download"
                    style={{
                      transform: 'scale(0.8)',
                    }}
                  />
                </IconButton>
              )}
            </Stack>
          )}
          {actions && actions}
        </Stack>
      </Stack>
      {contentBetweenToolbarAndTable}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <Suspense fallback={<Skeleton variant="rectangular" height={400} />}>
          <VyTable
            apiRef={apiRef}
            tableName={tableName}
            columns={stableColumns}
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
            sx={{
              flex: 1,
              minHeight: 0,
              '& .MuiDataGrid-columnHeaderTitle': {
                fontSize: '14px',
                lineHeight: '24px',
                fontFamily: 'Inter, sans-serif',
                color: '#7E8796',
              },
              '& .MuiDataGrid-row': {
                borderBottom: '1px solid #E8EBF2',
                cursor: tableProps.onRowClick ? 'pointer' : 'default',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#F5F6FA',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #E8EBF2',
              },
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: '1px solid #E8EBF2',
              },
              '& .MuiDataGrid-row .MuiDataGrid-cell': {
                backgroundColor: '#FFFFFF',
              },
              '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
                backgroundColor: '#F5F6FA',
              },
              '& .hovered-row': {
                backgroundColor: '#E8EBF2',
                '&:hover': {
                  backgroundColor: '#E8EBF2',
                },
                '& .MuiDataGrid-cell': {
                  backgroundColor: '#E8EBF2 !important',
                },
              },
              '& .MuiDataGrid-row.Mui-selected': {
                backgroundColor: '#E8EBF2',
                '&:hover': {
                  backgroundColor: '#E8EBF2',
                },
              },
              '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell': {
                backgroundColor: '#E8EBF2',
              },
              ...tableSx,
            }}
            {...tableProps}
          />
        </Suspense>
        {tableFooter}
      </Box>
    </Box>
  );
}
